const API_BASE_URL = (import.meta as any).env?.VITE_ADMIN_AUTH_API_URL || 'http://localhost:8081'

export interface LoginRequest {
  email: string
  password: string
}

export interface LoginResponse {
  access_token: string
  token_type: string
  admin: AdminResponse
}

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export interface AdminResponse {
  id: string
  email: string
  role: string
  tenant_id?: string
  tenant_name?: string
  is_active: boolean
  created_at: string
}

export interface TaskResponse {
  id: string
  schedule_name: string
  frequency: string
  tenant_id?: string
  tenant_name?: string
  last_run?: string
  next_run?: string
  is_active: boolean
  created_at: string,
  task_extra_data: Record<string, any>
}

export interface AdminUpdateRequest {
  email?: string
  current_password?: string
  new_password?: string
}

export interface UserUpdateRequest {
  role?: string
}

export interface TenantLog {
  id?: string
  created_at: string
  message: string
  current_status: string
}

export interface CreateTenantChecklist {
  tenant_created_on_lawmabase: boolean
  dns_entries_added: boolean
  custom_domains_added_in_azure: boolean
  ssl_certificates_created_and_tested: boolean
}

export interface SdlOnboardingChecklist {
  crawl_mds_uploaded: boolean
  marketing_audit_uploaded: boolean
  competitor_crawl_uploaded: boolean
  questionaire_uploaded: boolean
  other_mds_uploaded: boolean
  sdl_generated: boolean
  sdl_uploaded_to_qm: boolean
  complete_sdl_qa_pass: boolean
}

export interface BuildClientContentChecklist {
  assets_added: boolean
  faq_category_labels_created: boolean
  ctas_setup: boolean
  homepages_and_variations_setup: boolean
  production_key_created: boolean
  preview_key_created: boolean
  knowledgebase_sync_job_added: boolean
  reviews_sync_job_added: boolean
  integrations_added: boolean
  appointment_scheduling_configured: boolean
  widget_configuration_done: boolean
  contact_settings_adjusted: boolean
  advanced_prompt_customized: boolean
  ama_fully_tested: boolean
  questions_fully_tested: boolean
}

export interface TenantResponse {
  id: string
  name: string
  subdomain: string
  status: string
  tenant_type: string
  created_at: string
  updated_at: string
  settings: Record<string, any>
  goal?: string
  aggressiveness?: number
  admin_count: number
  build_date?: string
  tenant_logs?: TenantLog[]
  sdl_onboarding_checklist?: SdlOnboardingChecklist
  create_tenant_checklist?: CreateTenantChecklist
  build_client_content_checklist?: BuildClientContentChecklist
  client_admin_enabled: boolean
  domain_expert_enabled: boolean
  app_subdomain: string | null
}

export interface TenantCreateRequest {
  name: string
  subdomain: string
  tenant_type?: string
  settings?: Record<string, any>
  goal?: string
  aggressiveness?: number
  client_admin_enabled?: boolean
  domain_expert_enabled?: boolean
  app_subdomain?: string
}

export interface TenantUpdateRequest {
  name?: string
  status?: string
  tenant_type?: string
  settings?: Record<string, any>
  goal?: string
  aggressiveness?: number
  sdl_onboarding_checklist?: SdlOnboardingChecklist
  create_tenant_checklist?: CreateTenantChecklist
  build_client_content_checklist?: BuildClientContentChecklist
  client_admin_enabled?: boolean
  domain_expert_enabled?: boolean
  app_subdomain?: string
}

export interface TenantAdminCreateRequest {
  email: string
  password: string
  role?: string
  tenant_id: string
}

export interface TaskCreateRequest {
  tenant_id: string
  schedule_name: string
  frequency: string
  extra_data: Record<string, any>
}

export interface GlobalTaskResponse {
  id: string
  schedule_name: string
  frequency: string
  last_run?: string
  next_run?: string
  created_at: string
  updated_at: string
  is_active: boolean
  task_extra_data: Record<string, any>
  member_count?: number
  active_member_count?: number
  latest_run_id?: string | null
  latest_run_status?: string | null
  latest_run_started_at?: string | null
  latest_run_failed_count?: number | null
}

export interface GlobalTaskCreateRequest {
  schedule_name: string
  frequency: string
  extra_data?: Record<string, any>
}

export interface GlobalTaskMemberResponse {
  id: string
  tenant_id: string
  tenant_name: string
  is_active: boolean
  period_status?: string | null
  last_started_at?: string | null
  last_ended_at?: string | null
}

export interface GlobalTaskRunResponse {
  id: string
  global_task_id: string
  period_key: string
  trigger: string
  started_at: string
  ended_at?: string | null
  status: string
  triggered_by?: string | null
}

export interface GlobalTaskTenantRunResponse {
  id: string
  global_task_run_id?: string | null
  global_task_id: string
  tenant_id: string
  tenant_name: string
  period_key: string
  started_at?: string | null
  ended_at?: string | null
  status: string
  error_message?: string | null
  result_json?: Record<string, any> | null
  attempt: number
}


export interface GooglePlaceIDGenerator {
  tenant_name: string
  city: string
  state: string
  country: string
}

export interface UserAnalytics {
  total_users: number
  users_by_role: Record<string, number>
  users_by_status: {
    active: number
    inactive: number
  }
  recent_registrations: number
  users_by_tenant: Array<{ tenant: string; count: number }>
}

export interface TenantAnalytics {
  total_tenants: number
  tenants_by_status: Record<string, number>
  tenant_growth: Array<{ month: string; count: number }>
  tenants_with_admins: Array<{
    name: string
    status: string
    admin_count: number
  }>
  recent_tenants: number
}

export interface ApiKeyCreateRequest {
  tenant_id: string;
  name?: string;
  description?: string;
  allowed_origins?: string[];
  expires_at?: string; // ISO 8601 string
}

export interface ApiKeyResponse {
  id: string;
  tenant_id: string;
  name?: string;
  description?: string;
  allowed_origins?: string[];
  is_active: boolean;
  created_at: string;
  last_used_at?: string;
  expires_at?: string;
}

export interface ApiKeyCreateResponse extends ApiKeyResponse {
  api_key: string; // The plain API key, only returned on creation
}

export interface BaseTemplate {
  id: string;
  prompt_type: string;
  template: string;
  name: string;
  description: string;
  variables: string[];
  is_active: boolean;
  version: number;
  created_by: string;
  created_at?: string;
  updated_at?: string;
}

export interface BaseTemplateResponse {
  prompts: BaseTemplate[];
  total: number;
}

export interface BaseTemplateRequest {
  template: string;
}

export interface PromptTypesResponse {
  prompt_types: string[];
}

export interface BasePromptPreviewRequest {
  template: string;
  variables: {
    goal: string;
    current_heat_category_name: string;
  };
}

export interface SystemEmbeddingResponse {
  id: string;
  name: string;
  command_type: string;
  text_content: string;
  created_at: string;
  updated_at: string;
}

export interface SystemEmbeddingListResponse {
  status: string;
  embeddings: SystemEmbeddingResponse[];
  total: number;
}

export interface SystemEmbeddingCreateRequest {
  name: string;
  command_type: string;
  content: string;
}

export interface BasePromptPreviewResponse {
  rendered_content: string;
  variables_used: string[];
  missing_variables: string[];
}

// ─── LARS / Labs types ───────────────────────────────────────────────────────

export interface LarsStageStatus {
  stage_number: number
  stage_name: string
  completed: boolean
  last_updated: string | null
}

export interface LarsRun {
  run_name: string
  stages: LarsStageStatus[]
  completed_count: number
  total_stages: number
  log_count: number
  recent_logs: string[]
  sdl_exists: boolean
  sdl_question_count: number | null
}

export interface LarsRunsListResponse {
  runs: LarsRun[]
  total_runs: number
}

export interface LarsJob {
  job_id: string
  run_name: string
  stage_number: number
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled'
  created_at: string
  started_at: string | null
  completed_at: string | null
  result: Record<string, any> | null
  error: string | null
  log_file: string | null
}

export interface LarsCreateRunRequest {
  run_name: string
  template_config?: string
  config_overrides?: Record<string, any>
}

export interface LarsUploadSdlResponse {
  status: string
  run_name: string
  sdl_path: string
  question_count: number
  message: string
}

export interface BulkImportConflict {
  question_text: string
  existing_id: string
}

export interface BulkImportResponse {
  total_submitted: number
  total_created: number
  total_skipped: number
  conflicts: BulkImportConflict[]
  created_ids: string[]
}

export interface TenantQuestionCounts {
  total: number
  lars_enabled: number
  sdl_questions: number
}

export interface SdlDeleteResult {
  questions_deleted: number
  embeddings_deleted: number
  metadata_cleared: boolean
}

export interface PushSdlToLarsResponse {
  status: string
  message: string
  question_count: number
}

export interface TenantSdlMetadata {
  brand: string | null
  industry: string | null
  last_updated: string | null
  strategic_foci: unknown | null
  variables: unknown | null
  competitors: unknown | null
  entity_model_summary: unknown | null
}

export interface LarsRunStageResponse {
  job_id: string
  run_name: string
  stage_number: number
  status: string
  poll_url: string
}

export interface LarsReportSummary {
  id: string
  lars_report_id: string
  run_name: string
  overall_grade: string
  last_synced: string
  is_viewable: boolean
  created_at: string
}

export interface LarsReportsResponse {
  status: string
  reports: LarsReportSummary[]
  total: number
}

// ─── SOT File Management types ───────────────────────────────────────────────

export interface SOTFileEntry {
  name: string
  path: string
  size: number
  last_modified: string | null
  content_type: string | null
}

export interface SOTFolderEntry {
  name: string
  path: string
}

export interface SOTListResponse {
  folders: SOTFolderEntry[]
  files: SOTFileEntry[]
}

export interface SOTUploadResponse {
  uploaded: { name: string; path: string; size: number }[]
  errors: string[]
}

export interface SOTDeleteResponse {
  deleted: string[]
  errors: string[]
}

export type LarsConfig = Record<string, any>

export interface LarsConfigUpdateResponse {
  status: string
  target?: string
  run_name?: string
}

// ─── SDL Generator / Labs types ──────────────────────────────────────────────

export interface SdlStageStatus {
  stage_number: number
  stage_name: string
  completed: boolean
  last_updated: string | null
}

export interface SdlInputCategorySummary {
  path: string
  file_count: number
}

export interface SdlOutputArtifact {
  name: string
  exists: boolean
  size: number | null
  last_updated: string | null
}

export interface SdlRun {
  run_name: string
  stages: SdlStageStatus[]
  completed_count: number
  total_stages: number
  tenant_id: string | null
  subdomain: string | null
  company_name: string | null
  industry_type: string | null
  inputs: Record<string, SdlInputCategorySummary> | null
  outputs: SdlOutputArtifact[] | null
  log_count: number
  recent_logs: string[]
}

export interface SdlRunsListResponse {
  runs: SdlRun[]
  total_runs: number
}

export interface SdlJob {
  job_id: string
  run_name: string
  stage_number: number
  variant: string | null
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled'
  created_at: string
  started_at: string | null
  completed_at: string | null
  result: Record<string, any> | null
  error: string | null
  log_file: string | null
}

export interface SdlCreateRunRequest {
  run_name: string
  config_overrides?: Record<string, any>
}

export interface SdlRunStageResponse {
  job_id: string
  run_name: string
  stage_number: number
  variant: string | null
  status: string
  poll_url: string
}

export interface SdlStageResult {
  run_name: string
  stage_number: number
  stage_name: string
  output_file: string | null
  last_updated: string | null
  summary: Record<string, any>
}

export interface SdlInputFile {
  name: string
  path: string
  size: number
  last_updated: string | null
}

export interface SdlInputsResponse {
  run_name: string
  categories: Record<string, { path: string; files: SdlInputFile[] }>
  total_files: number
}

export interface SdlSotSyncFile {
  source: string
  category: string
  dest_name?: string
}

export interface SdlSotSyncRequest {
  subdomain: string
  files?: SdlSotSyncFile[]
  auto?: boolean
}

export interface SdlSotSyncResponse {
  run_name: string
  subdomain: string
  auto: boolean
  copied: { source: string; dest: string; category: string; size: number }[]
  failed: { source: string; error: string }[]
  total_copied: number
  total_failed: number
}

export interface SdlOutputsResponse {
  run_name: string
  outputs: SdlOutputArtifact[]
}

export type SdlConfig = Record<string, any>

// ─── Feedback types ───────────────────────────────────────────────────────────

export interface FeedbackItem {
  id: string;
  tenant_id: string;
  conversation_id: string | null;
  session_id: string | null;
  message_id: string | null;
  question: string;
  original_answer: string;
  source_type: string | null;
  rating: 'thumbs_up' | 'thumbs_down';
  feedback_categories: string[] | null;
  feedback_details: string | null;
  suggested_answer: string | null;
  created_at: string;
}

export interface FeedbackListResponse {
  items: FeedbackItem[];
  total: number;
  skip: number;
  limit: number;
}

export interface FeedbackQueryParams {
  tenant_id?: string;
  rating?: 'thumbs_up' | 'thumbs_down';
  source_type?: 'chat' | 'faq_ama';
  date_from?: string;
  date_to?: string;
  skip?: number;
  limit?: number;
}

// ─── Domain Expert Agents types ───────────────────────────────────────────────

export type ExpertStatus = 'invited' | 'active' | 'disabled'

export interface ExpertItem {
  id: string
  email: string
  full_name: string
  status: ExpertStatus
  invite_state: 'pending' | 'expired' | 'none'
  invite_expires_at: string | null
  sign_in_methods: string[]
  signup_source: 'invite' | 'google_auto_join'
  last_login_at: string | null
  last_seen_at: string | null
  created_at: string
  created_by: { type: string; email: string | null }
}

export interface ExpertCounts {
  invited: number
  active: number
  disabled: number
}

export interface ExpertList {
  items: ExpertItem[]
  total: number
  counts: ExpertCounts
}

export interface ExpertListParams {
  status?: ExpertStatus
  search?: string
  skip?: number
  limit?: number
}

export interface ExpertCreateRequest {
  email: string
  full_name: string
  send_invite?: boolean
}

export interface ExpertUpdateRequest {
  full_name?: string
  email?: string
  status?: 'active' | 'disabled'
}

export interface CreateExpertResult {
  expert: ExpertItem
  invite_url: string | null
  invite_expires_at: string | null
  email_sent: boolean
}

export interface ExpertInviteResult {
  invite_url: string
  invite_expires_at: string
  email_sent: boolean
}

export interface ExpertResetResult {
  reset_url: string
  expires_at: string
  email_sent: boolean
}

export interface ExpertSignInSettings {
  google_sso_enabled: boolean
  allowed_google_domains: string[]
  auto_join_enabled: boolean
  password_login_enabled: boolean
  login_url: string
  app_subdomain: string
}

export interface ExpertSignInUpdateRequest {
  google_sso_enabled: boolean
  allowed_google_domains: string[]
  auto_join_enabled: boolean
  password_login_enabled: boolean
}

export interface ExpertAppSettings {
  app_name: string | null
  app_short_name: string | null
  app_icon_url: string | null
  primary_color: string | null
  welcome_message: string | null
  suggested_prompts: string[]
  app_subdomain: string
  effective: Record<string, any>
  config_version: string
}

export interface DomainExpertAdminView {
  enabled: boolean
  sign_in: ExpertSignInSettings
  app: ExpertAppSettings
  enabled_at: string | null
  enabled_by_email: string | null
  counts: ExpertCounts
}

export interface ExpertAgentSummary {
  documents_ready: number
  documents_processing: number
  documents_failed: number
  saved_answers: number
  open_flags: number
  saved_answers_missing_embeddings: number
  documents_missing_embeddings: number
}

export interface ExpertEmbeddingsRegenerateResult {
  saved_answers: number
  documents: number
}

export interface ExpertAdminFlag {
  id: string
  question: string
  answer: string
  sources: any[]
  reason_category: string
  comment: string | null
  suggested_answer: string | null
  status: 'open' | 'resolved' | 'dismissed' | 'withdrawn'
  resolution_note: string | null
  resolution_question_id: string | null
  flagged_by: { id: string; email: string; name: string }
  conversation_id: string | null
  message_id: string | null
  created_at: string
  resolved_at: string | null
  resolved_by_email: string | null
}

export interface ExpertFlagList {
  items: ExpertAdminFlag[]
  total: number
  counts: { open: number; done: number }
}

export class ApiError extends Error {
  status: number
  code?: string

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

// ─── Role Permission types ────────────────────────────────────────────────────
// Role Permission types — flat format: { "default": "full", "/keys": "hidden", ... }
export type RolePermissionEntry = Record<string, string>;

export type RolePermissionsResponse = Record<string, RolePermissionEntry>;

export type UpdateRolePermissionsRequest = Record<string, RolePermissionEntry>;

export type ChatProvider = 'azure_openai' | 'azure_anthropic';

export type ChatEffort = 'low' | 'medium' | 'high';

export interface ChatSettings {
  provider: ChatProvider;
  provider_source: 'admin' | 'environment';
  effort: ChatEffort;
  effort_source: 'admin' | 'environment';
  claude_model: string;
  gpt_deployment: string;
  configured_providers: ChatProvider[];
  updated_by: string | null;
  updated_at: string | null;
}

export interface ChatSettingsUpdateRequest {
  provider: ChatProvider;
  effort: ChatEffort;
}

class ApiClient {
  private baseURL: string
  private token: string | null = null

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL
    this.token = localStorage.getItem('admin_token')
  }

  setToken = (token: string) => {
    this.token = token
    localStorage.setItem('admin_token', token)
  }

  clearToken = () => {
    this.token = null
    localStorage.removeItem('admin_token')
  }

  private request = async <T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> => {
    const url = `${this.baseURL}${endpoint}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error(`API Error for ${endpoint}:`, {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        })
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`
        let errorCode: string | undefined

        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Pydantic validation errors
            errorMessage = errorData.detail.map((err: any) => `${err.loc?.join('.') || 'field'}: ${err.msg}`).join(', ')
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail
          } else if (typeof errorData.detail === 'object') {
            errorCode = errorData.detail.code
            if (errorData.detail.message) errorMessage = errorData.detail.message
          }
        } else if (errorData.message) {
          errorMessage = errorData.message
        }

        throw new ApiError(errorMessage, response.status, errorCode)
      }

      if (response.status === 204) return undefined as T

      const data = await response.json()
      return data
    } catch (error) {
      console.error(`Request failed for ${endpoint}:`, error)
      throw error
    }
  }

  // Authentication endpoints
  login = async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await this.request<LoginResponse>('/api/v1/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })

    this.setToken(response.access_token)
    return response
  }

  logout = async (): Promise<void> => {
    this.clearToken()
  }

  getCurrentAdmin = async (): Promise<AdminResponse> => {
    return this.request<AdminResponse>('/api/v1/admin/auth/me')
  }

  updateCurrentAdmin = async (updates: AdminUpdateRequest): Promise<AdminResponse> => {
    return this.request<AdminResponse>('/api/v1/admin/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  }

  // Tenant management endpoints
  getTenants = async (): Promise<TenantResponse[]> => {
    return this.request<TenantResponse[]>('/api/v1/admin/tenants')
  }

  createTenant = async (tenant: TenantCreateRequest): Promise<TenantResponse> => {
    return this.request<TenantResponse>('/api/v1/admin/tenants', {
      method: 'POST',
      body: JSON.stringify(tenant),
    })
  }

  updateTenant = async (
    tenantId: string,
    updates: TenantUpdateRequest
  ): Promise<TenantResponse> => {
    return this.request<TenantResponse>(`/api/v1/admin/tenants/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  }

  deleteTenant = async (tenantId: string): Promise<{ message: string }> => {
    return this.request<{ message: string }>(`/api/v1/admin/tenants/${tenantId}`, {
      method: 'DELETE',
    })
  }

  getTenantLogs = async (tenantId: string): Promise<TenantLog[]> => {
    return this.request<TenantLog[]>(`/api/v1/admin/tenants/${tenantId}/logs`)
  }

  clearTenantCache = async (tenantId: string): Promise<{ success: boolean; message: string; tenant_id: string; details: any }> => {
    return this.request<{ success: boolean; message: string; tenant_id: string; details: any }>(`/api/v1/admin/tenants/${tenantId}/clear-cache`, {
      method: 'POST',
    })
  }

  // Tenant Admin management endpoints
  getUsers = async (): Promise<AdminResponse[]> => {
    return this.request<AdminResponse[]>('/api/v1/admin/tenant-admins')
  }

  createUser = async (user: TenantAdminCreateRequest): Promise<AdminResponse> => {
    return this.request<AdminResponse>('/api/v1/admin/tenant-admins', {
      method: 'POST',
      body: JSON.stringify(user),
    })
  }

  updateUser = async (userId: string, data: UserUpdateRequest): Promise<AdminResponse> => {
    return this.request<AdminResponse>(`/api/v1/admin/tenant-admins/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  deleteUser = async (userId: string): Promise<{ message: string }> => {
    return this.request<{ message: string }>(`/api/v1/admin/tenant-admins/${userId}`, {
      method: 'DELETE',
    })
  }

  // Analytics endpoints
  getUserAnalytics = async (): Promise<UserAnalytics> => {
    return this.request<UserAnalytics>('/api/v1/admin/analytics/users')
  }

  getTenantAnalytics = async (): Promise<TenantAnalytics> => {
    return this.request<TenantAnalytics>('/api/v1/admin/analytics/tenants')
  }

  // API Key management endpoints
  createApiKey = async (apiKeyData: ApiKeyCreateRequest): Promise<ApiKeyCreateResponse> => {
    return this.request<ApiKeyCreateResponse>('/api/v1/admin/api-keys', {
      method: 'POST',
      body: JSON.stringify(apiKeyData),
    });
  }

  getApiKeys = async (tenantId: string): Promise<ApiKeyResponse[]> => {
    return this.request<ApiKeyResponse[]>(`/api/v1/admin/api-keys/${tenantId}`);
  }

  deleteApiKey = async (apiKeyId: string): Promise<{ message: string }> => {
    return this.request<{ message: string }>(`/api/v1/admin/api-keys/${apiKeyId}`, {
      method: 'DELETE',
    });
  }

  toggleApiKeyStatus = async (apiKeyId: string): Promise<{ message: string; is_active: boolean }> => {
    return this.request<{ message: string; is_active: boolean }>(`/api/v1/admin/api-keys/${apiKeyId}/toggle`, {
      method: 'PATCH',
    });
  }

  // Base Template management endpoints (Super Admin only)
  getBaseTemplates = async (): Promise<BaseTemplateResponse> => {
    return this.request<BaseTemplateResponse>('/api/v1/admin/prompts/templates');
  }

  updateBaseTemplate = async (
    promptType: string,
    templateData: BaseTemplateRequest
  ): Promise<{ message: string }> => {
    return this.request<{ message: string }>(`/api/v1/admin/prompts/templates/${promptType}`, {
      method: 'PUT',
      body: JSON.stringify(templateData),
    });
  }

  getPromptTypes = async (): Promise<PromptTypesResponse> => {
    return this.request<PromptTypesResponse>('/api/v1/admin/prompts/types');
  }

  previewBasePrompt = async (
    previewData: BasePromptPreviewRequest
  ): Promise<BasePromptPreviewResponse> => {
    return this.request<BasePromptPreviewResponse>('/api/v1/admin/prompts/preview', {
      method: 'POST',
      body: JSON.stringify(previewData),
    });
  }


  getTasks = async (): Promise<TaskResponse[]> => {
    return this.request<TaskResponse[]>('/api/v1/admin/tasks')
  }

  deleteTasks = async (tenantId: string, taskId: string): Promise<any> => {
    return this.request<AdminResponse>(`/api/v1/admin/tasks/${tenantId}/schedule/${taskId}`, {
      method: 'DELETE',
    })
  }

  createTask = async (task: TaskCreateRequest): Promise<any> => {
    return this.request<AdminResponse>(`/api/v1/admin/tasks/schedule`, {
      method: 'POST',
      body: JSON.stringify(task),
    })
  }

  generateGooglePlaceId = async (request: GooglePlaceIDGenerator): Promise<any> => {
    return this.request<AdminResponse>(`/api/v1/admin/google/places/id/generate`, {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  generateGoogleOauthToken = async (connectCode: string): Promise<any> => {
    // Call AI API webhook endpoint directly (bypasses tenant identification)
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/webhooks/google/oauth/token`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ connect_code: connectCode }),
      })
      if (!response.ok) {
        throw new Error("Unable to generate Google OAuth token")
      }

      const result = await response.json()
      return result.result || result

    } catch (error) {
      console.error('Error generating Google OAuth token:', error)
      throw error
    }
  }

  getGlobalTasks = async (): Promise<GlobalTaskResponse[]> => {
    return this.request<GlobalTaskResponse[]>('/api/v1/admin/global-tasks')
  }

  createGlobalTask = async (task: GlobalTaskCreateRequest): Promise<any> => {
    return this.request<AdminResponse>('/api/v1/admin/global-tasks/schedule', {
      method: 'POST',
      body: JSON.stringify(task),
    })
  }

  deleteGlobalTask = async (globalTaskId: string): Promise<any> => {
    return this.request<AdminResponse>(`/api/v1/admin/global-tasks/${globalTaskId}`, {
      method: 'DELETE',
    })
  }

  getGlobalTask = async (globalTaskId: string): Promise<GlobalTaskResponse> => {
    return this.request<GlobalTaskResponse>(`/api/v1/admin/global-tasks/${globalTaskId}`)
  }

  getGlobalTaskMembers = async (globalTaskId: string): Promise<GlobalTaskMemberResponse[]> => {
    return this.request<GlobalTaskMemberResponse[]>(`/api/v1/admin/global-tasks/${globalTaskId}/members`)
  }

  updateGlobalTaskMember = async (
    globalTaskId: string,
    tenantId: string,
    isActive: boolean,
  ): Promise<any> => {
    return this.request<AdminResponse>(`/api/v1/admin/global-tasks/${globalTaskId}/members/${tenantId}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_active: isActive }),
    })
  }

  getGlobalTaskRuns = async (globalTaskId: string): Promise<GlobalTaskRunResponse[]> => {
    return this.request<GlobalTaskRunResponse[]>(`/api/v1/admin/global-tasks/${globalTaskId}/runs`)
  }

  getGlobalTaskTenantRuns = async (
    globalTaskId: string,
    runId: string,
  ): Promise<GlobalTaskTenantRunResponse[]> => {
    return this.request<GlobalTaskTenantRunResponse[]>(
      `/api/v1/admin/global-tasks/${globalTaskId}/runs/${runId}/tenants`,
    )
  }

  runGlobalTaskNow = async (
    globalTaskId: string,
    options?: { force?: boolean; tenantId?: string; stages?: number[] },
  ): Promise<any> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/webhooks/execute-global-task`
    const currentAdmin = await this.getCurrentAdmin()

    const payload: Record<string, any> = {
      global_task_id: globalTaskId,
      super_admin_id: currentAdmin.id,
      timestamp: new Date().toISOString(),
      force: options?.force ?? false,
      tenant_id: options?.tenantId ?? null,
    }
    if (options?.stages?.length) {
      payload.stages = options.stages
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}`)
    }

    return response.json()
  }

  runTaskNow = async (taskId: string): Promise<any> => {
    // Call AI API webhook endpoint directly (bypasses tenant identification)
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/webhooks/execute-task`

    // Get current admin info to include super_admin_id
    const currentAdmin = await this.getCurrentAdmin()

    const payload = {
      task_id: taskId,
      super_admin_id: currentAdmin.id,
      timestamp: new Date().toISOString()
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`

        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail
          }
        } else if (errorData.message) {
          errorMessage = errorData.message
        }

        throw new Error(errorMessage)
      }

      const result = await response.json()
      // Return the inner result data to match expected response format
      return result.result || result
    } catch (error) {
      console.error(`Request failed for ${url}:`, error)
      throw error
    }
  }

  // Role Permission endpoints
  getRolePermissions = async (): Promise<RolePermissionsResponse> => {
    return this.request<RolePermissionsResponse>('/api/v1/admin/roles/permissions');
  }

  updateRolePermissions = async (data: UpdateRolePermissionsRequest): Promise<RolePermissionsResponse> => {
    return this.request<RolePermissionsResponse>('/api/v1/admin/roles/permissions', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // System Embedding endpoints
  getSystemEmbeddings = async (): Promise<SystemEmbeddingResponse[]> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/webhooks/embeddings/admin/system-embeddings`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }

    const response = await fetch(url, { headers })
    if (!response.ok) throw new Error('Failed to fetch system embeddings')
    const result: SystemEmbeddingListResponse = await response.json()
    return result.embeddings
  }

  createSystemEmbedding = async (data: SystemEmbeddingCreateRequest): Promise<any> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/webhooks/embeddings/admin/system-embeddings`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    })
    if (!response.ok) throw new Error('Failed to create system embedding')
    return response.json()
  }

  updateSystemEmbedding = async (id: string, data: SystemEmbeddingCreateRequest): Promise<any> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/webhooks/embeddings/admin/system-embeddings/${id}`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    })
    if (!response.ok) throw new Error('Failed to update system embedding')
    return response.json()
  }

  deleteSystemEmbedding = async (id: string): Promise<any> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/webhooks/embeddings/admin/system-embeddings/${id}`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    })
    if (!response.ok) throw new Error('Failed to delete system embedding')
    return response.json()
  }

  // ─── LARS / Labs endpoints ─────────────────────────────────────────────────

  getLarsRuns = async (opts?: { summary?: boolean }): Promise<LarsRunsListResponse> => {
    const qs = opts?.summary ? '?summary=true' : ''
    return this.request<LarsRunsListResponse>(`/api/v1/admin/lars/runs${qs}`)
  }

  getLarsRun = async (runName: string): Promise<LarsRun> => {
    return this.request<LarsRun>(`/api/v1/admin/lars/runs/${encodeURIComponent(runName)}`)
  }

  createLarsRun = async (req: LarsCreateRunRequest): Promise<{ status: string; run_name: string; message: string }> => {
    return this.request<{ status: string; run_name: string; message: string }>('/api/v1/admin/lars/runs', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  deleteLarsRun = async (runName: string): Promise<{ status: string; run_name: string }> => {
    return this.request<{ status: string; run_name: string }>(`/api/v1/admin/lars/runs/${encodeURIComponent(runName)}`, {
      method: 'DELETE',
    })
  }

  uploadLarsSDL = async (runName: string, sdlData: Record<string, any>): Promise<LarsUploadSdlResponse> => {
    return this.request<LarsUploadSdlResponse>(`/api/v1/admin/lars/runs/${encodeURIComponent(runName)}/sdl`, {
      method: 'POST',
      body: JSON.stringify(sdlData),
    })
  }

  // Import SDL questions into a tenant's entity model (ai-api webhook, super-admin only).
  importQuestionsToEntityModel = async (tenantId: string, sdlData: Record<string, any>): Promise<BulkImportResponse> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/webhooks/questions/bulk-import`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ...sdlData, tenant_id: tenantId }),
    })
    if (!response.ok) {
      let detail = 'Failed to import questions'
      try {
        const err = await response.json()
        detail = err?.detail || detail
      } catch { /* ignore */ }
      throw new Error(detail)
    }
    const result = await response.json()
    return result.data as BulkImportResponse
  }

  // Tenant primer question counts (total + LARS-enabled) — derived from the client project (ai-api).
  getTenantQuestionCounts = async (tenantId: string): Promise<TenantQuestionCounts> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/webhooks/questions/counts?tenant_id=${encodeURIComponent(tenantId)}`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }

    const response = await fetch(url, { headers })
    if (!response.ok) throw new Error('Failed to fetch question counts')
    const result = await response.json()
    return result.data as TenantQuestionCounts
  }

  // Delete a tenant's SDL-imported questions and clear all SDL metadata (ai-api webhook, super-admin only).
  deleteTenantSdl = async (tenantId: string): Promise<SdlDeleteResult> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/webhooks/questions/sdl?tenant_id=${encodeURIComponent(tenantId)}`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }

    const response = await fetch(url, { method: 'DELETE', headers })
    if (!response.ok) {
      let detail = 'Failed to delete SDL'
      try {
        const err = await response.json()
        detail = err?.detail || detail
      } catch { /* ignore */ }
      throw new Error(detail)
    }
    const result = await response.json()
    return result.data as SdlDeleteResult
  }

  // All SDL metadata sections for a tenant (ai-api webhook, super-admin only).
  getTenantSdlMetadata = async (tenantId: string): Promise<TenantSdlMetadata> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/webhooks/questions/sdl-metadata?tenant_id=${encodeURIComponent(tenantId)}`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }

    const response = await fetch(url, { headers })
    if (!response.ok) {
      let detail = 'Failed to fetch SDL metadata'
      try {
        const err = await response.json()
        detail = err?.detail || detail
      } catch { /* ignore */ }
      throw new Error(detail)
    }
    const result = await response.json()
    return result.data as TenantSdlMetadata
  }

  // Partially update a tenant's SDL metadata — only the sections present in the patch are written.
  updateTenantSdlMetadata = async (tenantId: string, patch: Partial<TenantSdlMetadata>): Promise<TenantSdlMetadata> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/webhooks/questions/sdl-metadata`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ tenant_id: tenantId, ...patch }),
    })
    if (!response.ok) {
      let detail = 'Failed to update SDL metadata'
      try {
        const err = await response.json()
        detail = err?.detail || detail
      } catch { /* ignore */ }
      throw new Error(detail)
    }
    const result = await response.json()
    return result.data as TenantSdlMetadata
  }

  // Rebuild the tenant's SDL from its entity model and push it to the tenant's LARS run.
  pushTenantSdlToLars = async (tenantId: string, subdomain: string): Promise<PushSdlToLarsResponse> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/webhooks/questions/push-sdl-to-labs`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ tenant_id: tenantId, subdomain }),
    })
    if (!response.ok) {
      let detail = 'Failed to push SDL to LARS'
      try {
        const err = await response.json()
        detail = err?.detail || detail
      } catch { /* ignore */ }
      throw new Error(detail)
    }
    return response.json()
  }

  runLarsStage = async (runName: string, stageNumber: number, resume = false, retryFailed = false): Promise<LarsRunStageResponse> => {
    return this.request<LarsRunStageResponse>(`/api/v1/admin/lars/runs/${encodeURIComponent(runName)}/stages/${stageNumber}`, {
      method: 'POST',
      body: JSON.stringify({ resume, retry_failed: retryFailed }),
    })
  }

  getLarsJobStatus = async (jobId: string): Promise<LarsJob> => {
    return this.request<LarsJob>(`/api/v1/admin/lars/jobs/${jobId}`)
  }

  cancelLarsJob = async (jobId: string): Promise<{ job_id: string; cancelled: boolean; status: string }> => {
    return this.request<{ job_id: string; cancelled: boolean; status: string }>(
      `/api/v1/admin/lars/jobs/${jobId}/cancel`,
      { method: 'POST' },
    )
  }

  getLarsStageResult = async (runName: string, stageNumber: number): Promise<Record<string, any>> => {
    return this.request<Record<string, any>>(`/api/v1/admin/lars/runs/${encodeURIComponent(runName)}/stages/${stageNumber}/result`)
  }

  downloadLarsStageLog = async (runName: string, stageNumber: number): Promise<void> => {
    const url = `${this.baseURL}/api/v1/admin/lars/runs/${encodeURIComponent(runName)}/stages/${stageNumber}/log`
    const headers: Record<string, string> = {}
    if (this.token) headers.Authorization = `Bearer ${this.token}`
    const response = await fetch(url, { headers })
    if (!response.ok) throw new Error(`Failed to download log: HTTP ${response.status}`)
    const blob = await response.blob()
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `stage${stageNumber}_${runName}.log`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  downloadLarsReport = async (runName: string): Promise<void> => {
    const url = `${this.baseURL}/api/v1/admin/lars/runs/${encodeURIComponent(runName)}/report/pdf`
    const headers: Record<string, string> = {}
    if (this.token) headers.Authorization = `Bearer ${this.token}`
    const response = await fetch(url, { headers })
    if (!response.ok) throw new Error(`Failed to download report: HTTP ${response.status}`)
    const blob = await response.blob()
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `lars_report_${runName}.pdf`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  downloadLarsReportByReportId = async (runName: string, larsReportId: string): Promise<void> => {
    const url = `${this.baseURL}/api/v1/admin/lars/runs/${encodeURIComponent(runName)}/report/pdf/${encodeURIComponent(larsReportId)}`
    const headers: Record<string, string> = {}
    if (this.token) headers.Authorization = `Bearer ${this.token}`
    const response = await fetch(url, { headers })
    if (!response.ok) throw new Error(`Failed to download archived report: HTTP ${response.status}`)
    const blob = await response.blob()
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `lars_report_${larsReportId}.pdf`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  // ─── LARS Report management (webhook, super-admin only) ───────────────────

  getLarsReportsForTenant = async (tenantId: string): Promise<LarsReportSummary[]> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/webhooks/lars/reports?tenant_id=${encodeURIComponent(tenantId)}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }
    const response = await fetch(url, { headers })
    if (!response.ok) throw new Error(`Failed to fetch LARS reports: HTTP ${response.status}`)
    const result: LarsReportsResponse = await response.json()
    return result.reports
  }

  deleteLarsReport = async (reportId: string): Promise<void> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/webhooks/lars/reports/${encodeURIComponent(reportId)}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }
    const response = await fetch(url, { method: 'DELETE', headers })
    if (!response.ok) throw new Error(`Failed to delete LARS report: HTTP ${response.status}`)
  }

  deleteLarsReportsByRun = async (tenantId: string, runName: string): Promise<void> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/webhooks/lars/reports/by-run?tenant_id=${encodeURIComponent(tenantId)}&run_name=${encodeURIComponent(runName)}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }
    const response = await fetch(url, { method: 'DELETE', headers })
    if (!response.ok) throw new Error(`Failed to delete LARS reports for run: HTTP ${response.status}`)
  }

  getLarsAuthorityReport = async (runName: string): Promise<{
    report_id: string
    is_viewable: boolean
    overall_grade: string
    lars_run_name: string
    [key: string]: any
  } | null> => {
    try {
      return await this.request<any>(
        `/api/v1/admin/lars/runs/${encodeURIComponent(runName)}/report/authority`,
      )
    } catch {
      return null
    }
  }

  toggleLarsReportVisibility = async (
    runName: string,
    tenantId: string,
    isViewable: boolean,
  ): Promise<{ status: string; run_name: string; is_viewable: boolean }> => {
    return this.request<{ status: string; run_name: string; is_viewable: boolean }>(
      `/api/v1/admin/lars/runs/${encodeURIComponent(runName)}/report/authority/visibility`,
      {
        method: 'PATCH',
        body: JSON.stringify({ is_viewable: isViewable, tenant_id: tenantId }),
      },
    )
  }

  deleteLarsReportFromAdmin = async (
    reportId: string,
    runName: string,
    tenantId: string,
  ): Promise<{ status: string; report_id: string; run_name: string }> => {
    return this.request<{ status: string; report_id: string; run_name: string }>(
      `/api/v1/admin/lars/reports/${encodeURIComponent(reportId)}?run_name=${encodeURIComponent(runName)}&tenant_id=${encodeURIComponent(tenantId)}`,
      { method: 'DELETE' },
    )
  }

  // ─── Feedback endpoints (direct to ai-api, admin Bearer token) ───────────

  getFeedback = async (params: FeedbackQueryParams = {}): Promise<FeedbackListResponse> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const query = new URLSearchParams()
    if (params.tenant_id) query.set('tenant_id', params.tenant_id)
    if (params.rating) query.set('rating', params.rating)
    if (params.source_type) query.set('source_type', params.source_type)
    if (params.date_from) query.set('date_from', params.date_from)
    if (params.date_to) query.set('date_to', params.date_to)
    if (params.skip !== undefined) query.set('skip', String(params.skip))
    if (params.limit !== undefined) query.set('limit', String(params.limit))

    const url = `${AI_API_URL}/feedback${query.toString() ? `?${query}` : ''}`
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (this.token) headers.Authorization = `Bearer ${this.token}`

    const response = await fetch(url, { headers })
    if (!response.ok) throw new Error(`Failed to fetch feedback: HTTP ${response.status}`)
    return response.json()
  }

  // ─── Domain Expert Agents (admin-api proxy → client-mgmt-api) ──────────────

  getDomainExpert = async (tenantId: string): Promise<DomainExpertAdminView> => {
    return this.request<DomainExpertAdminView>(`/api/v1/admin/tenants/${tenantId}/domain-expert`)
  }

  updateDomainExpertSignIn = async (
    tenantId: string,
    data: ExpertSignInUpdateRequest,
  ): Promise<ExpertSignInSettings> => {
    return this.request<ExpertSignInSettings>(`/api/v1/admin/tenants/${tenantId}/domain-expert/sign-in`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  updateDomainExpertApp = async (
    tenantId: string,
    data: { app_subdomain: string },
  ): Promise<ExpertAppSettings> => {
    return this.request<ExpertAppSettings>(`/api/v1/admin/tenants/${tenantId}/domain-expert/app`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  listDomainExperts = async (tenantId: string, params: ExpertListParams = {}): Promise<ExpertList> => {
    const query = new URLSearchParams()
    if (params.status) query.set('status', params.status)
    if (params.search) query.set('search', params.search)
    if (params.skip !== undefined) query.set('skip', String(params.skip))
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const qs = query.toString()
    return this.request<ExpertList>(`/api/v1/admin/tenants/${tenantId}/domain-experts${qs ? `?${qs}` : ''}`)
  }

  createDomainExpert = async (tenantId: string, data: ExpertCreateRequest): Promise<CreateExpertResult> => {
    return this.request<CreateExpertResult>(`/api/v1/admin/tenants/${tenantId}/domain-experts`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  getDomainExpertItem = async (tenantId: string, expertId: string): Promise<ExpertItem> => {
    return this.request<ExpertItem>(`/api/v1/admin/tenants/${tenantId}/domain-experts/${expertId}`)
  }

  updateDomainExpert = async (
    tenantId: string,
    expertId: string,
    data: ExpertUpdateRequest,
  ): Promise<ExpertItem> => {
    return this.request<ExpertItem>(`/api/v1/admin/tenants/${tenantId}/domain-experts/${expertId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  deleteDomainExpert = async (tenantId: string, expertId: string): Promise<{ ok: boolean }> => {
    return this.request<{ ok: boolean }>(`/api/v1/admin/tenants/${tenantId}/domain-experts/${expertId}`, {
      method: 'DELETE',
    })
  }

  sendDomainExpertInvite = async (
    tenantId: string,
    expertId: string,
    sendEmail: boolean,
  ): Promise<ExpertInviteResult> => {
    return this.request<ExpertInviteResult>(`/api/v1/admin/tenants/${tenantId}/domain-experts/${expertId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ send_email: sendEmail }),
    })
  }

  cancelDomainExpertInvite = async (tenantId: string, expertId: string): Promise<{ ok: boolean }> => {
    return this.request<{ ok: boolean }>(`/api/v1/admin/tenants/${tenantId}/domain-experts/${expertId}/invite`, {
      method: 'DELETE',
    })
  }

  resetDomainExpertPassword = async (
    tenantId: string,
    expertId: string,
    sendEmail: boolean,
  ): Promise<ExpertResetResult> => {
    return this.request<ExpertResetResult>(
      `/api/v1/admin/tenants/${tenantId}/domain-experts/${expertId}/password-reset`,
      { method: 'POST', body: JSON.stringify({ send_email: sendEmail }) },
    )
  }

  revokeDomainExpertSessions = async (tenantId: string, expertId: string): Promise<{ revoked: number }> => {
    return this.request<{ revoked: number }>(
      `/api/v1/admin/tenants/${tenantId}/domain-experts/${expertId}/sessions/revoke`,
      { method: 'POST' },
    )
  }

  getDomainExpertLoginLink = async (tenantId: string): Promise<{ login_url: string; app_subdomain: string }> => {
    return this.request<{ login_url: string; app_subdomain: string }>(
      `/api/v1/admin/tenants/${tenantId}/domain-experts/login-link`,
    )
  }

  // ─── Platform chat model (admin-api proxy → ai-api) ─────────────────────────

  getChatSettings = async (): Promise<ChatSettings> => {
    return this.request<ChatSettings>('/api/v1/admin/platform/chat-settings')
  }

  updateChatSettings = async (data: ChatSettingsUpdateRequest): Promise<ChatSettings> => {
    return this.request<ChatSettings>('/api/v1/admin/platform/chat-settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // ─── Expert agent summary (direct to ai-api, admin Bearer token) ─────────

  private aiApiGet = async <T>(path: string): Promise<T> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (this.token) headers.Authorization = `Bearer ${this.token}`

    const response = await fetch(`${AI_API_URL}${path}`, { headers })
    if (!response.ok) throw new ApiError(`HTTP ${response.status}`, response.status)
    return response.json()
  }

  private aiApiPost = async <T>(path: string): Promise<T> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (this.token) headers.Authorization = `Bearer ${this.token}`

    const response = await fetch(`${AI_API_URL}${path}`, { method: 'POST', headers })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const detail = errorData?.detail
      throw new ApiError(detail?.message || `HTTP ${response.status}`, response.status, detail?.code)
    }
    return response.json()
  }

  getExpertAgentSummary = async (tenantId: string): Promise<ExpertAgentSummary> => {
    return this.aiApiGet<ExpertAgentSummary>(
      `/tenant/expert-agent/summary?tenant_id=${encodeURIComponent(tenantId)}`,
    )
  }

  getExpertAgentOpenFlags = async (tenantId: string, limit: number = 5): Promise<ExpertFlagList> => {
    return this.aiApiGet<ExpertFlagList>(
      `/tenant/expert-agent/flags?status=open&limit=${limit}&tenant_id=${encodeURIComponent(tenantId)}`,
    )
  }

  regenerateExpertEmbeddings = async (tenantId: string): Promise<ExpertEmbeddingsRegenerateResult> => {
    return this.aiApiPost<ExpertEmbeddingsRegenerateResult>(
      `/tenant/expert-agent/embeddings/regenerate?tenant_id=${encodeURIComponent(tenantId)}`,
    )
  }

  // ─── LARS Config endpoints ─────────────────────────────────────────────────

  getLarsTemplateConfig = async (): Promise<LarsConfig> => {
    return this.request<LarsConfig>('/api/v1/admin/lars/config/template')
  }

  updateLarsTemplateConfig = async (config: LarsConfig): Promise<LarsConfigUpdateResponse> => {
    return this.request<LarsConfigUpdateResponse>('/api/v1/admin/lars/config/template', {
      method: 'PUT',
      body: JSON.stringify({ config }),
    })
  }

  getLarsRunConfig = async (runName: string): Promise<LarsConfig> => {
    return this.request<LarsConfig>(`/api/v1/admin/lars/runs/${encodeURIComponent(runName)}/config`)
  }

  updateLarsRunConfig = async (runName: string, config: LarsConfig): Promise<LarsConfigUpdateResponse> => {
    return this.request<LarsConfigUpdateResponse>(`/api/v1/admin/lars/runs/${encodeURIComponent(runName)}/config`, {
      method: 'PUT',
      body: JSON.stringify({ config }),
    })
  }

  // ─── SDL Generator / Labs endpoints ─────────────────────────────────────────

  getSdlRuns = async (opts?: { summary?: boolean }): Promise<SdlRunsListResponse> => {
    const qs = opts?.summary ? '?summary=true' : ''
    return this.request<SdlRunsListResponse>(`/api/v1/admin/sdl/runs${qs}`)
  }

  getSdlRun = async (runName: string): Promise<SdlRun> => {
    return this.request<SdlRun>(`/api/v1/admin/sdl/runs/${encodeURIComponent(runName)}`)
  }

  createSdlRun = async (req: SdlCreateRunRequest): Promise<{ status: string; run_name: string; message: string }> => {
    return this.request<{ status: string; run_name: string; message: string }>('/api/v1/admin/sdl/runs', {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  deleteSdlRun = async (runName: string): Promise<{ status: string; run_name: string }> => {
    return this.request<{ status: string; run_name: string }>(`/api/v1/admin/sdl/runs/${encodeURIComponent(runName)}`, {
      method: 'DELETE',
    })
  }

  getSdlTemplateConfig = async (): Promise<SdlConfig> => {
    return this.request<SdlConfig>('/api/v1/admin/sdl/config/template')
  }

  updateSdlTemplateConfig = async (config: SdlConfig): Promise<LarsConfigUpdateResponse> => {
    return this.request<LarsConfigUpdateResponse>('/api/v1/admin/sdl/config/template', {
      method: 'PUT',
      body: JSON.stringify({ config }),
    })
  }

  getSdlRunConfig = async (runName: string): Promise<SdlConfig> => {
    return this.request<SdlConfig>(`/api/v1/admin/sdl/runs/${encodeURIComponent(runName)}/config`)
  }

  updateSdlRunConfig = async (runName: string, config: SdlConfig): Promise<LarsConfigUpdateResponse> => {
    return this.request<LarsConfigUpdateResponse>(`/api/v1/admin/sdl/runs/${encodeURIComponent(runName)}/config`, {
      method: 'PUT',
      body: JSON.stringify({ config }),
    })
  }

  runSdlStage = async (runName: string, stageNumber: number, variant?: string): Promise<SdlRunStageResponse> => {
    return this.request<SdlRunStageResponse>(`/api/v1/admin/sdl/runs/${encodeURIComponent(runName)}/stages/${stageNumber}`, {
      method: 'POST',
      body: JSON.stringify(variant ? { variant } : {}),
    })
  }

  getSdlJobStatus = async (jobId: string): Promise<SdlJob> => {
    return this.request<SdlJob>(`/api/v1/admin/sdl/jobs/${jobId}`)
  }

  cancelSdlJob = async (jobId: string): Promise<{ job_id: string; cancelled: boolean; status: string }> => {
    return this.request<{ job_id: string; cancelled: boolean; status: string }>(
      `/api/v1/admin/sdl/jobs/${jobId}/cancel`,
      { method: 'POST' },
    )
  }

  getSdlStageResult = async (runName: string, stageNumber: number): Promise<SdlStageResult> => {
    return this.request<SdlStageResult>(`/api/v1/admin/sdl/runs/${encodeURIComponent(runName)}/stages/${stageNumber}/result`)
  }

  downloadSdlStageLog = async (runName: string, stageNumber: number): Promise<void> => {
    const url = `${this.baseURL}/api/v1/admin/sdl/runs/${encodeURIComponent(runName)}/stages/${stageNumber}/log`
    const headers: Record<string, string> = {}
    if (this.token) headers.Authorization = `Bearer ${this.token}`
    const response = await fetch(url, { headers })
    if (!response.ok) throw new Error(`Failed to download log: HTTP ${response.status}`)
    const blob = await response.blob()
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `sdl_stage${stageNumber}_${runName}.log`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  listSdlInputs = async (runName: string): Promise<SdlInputsResponse> => {
    return this.request<SdlInputsResponse>(`/api/v1/admin/sdl/runs/${encodeURIComponent(runName)}/inputs`)
  }

  uploadSdlInputs = async (runName: string, category: string, files: File[], destPath?: string): Promise<SOTUploadResponse> => {
    const formData = new FormData()
    formData.append('category', category)
    if (destPath) formData.append('dest_path', destPath)
    files.forEach((file) => formData.append('files', file))

    const url = `${this.baseURL}/api/v1/admin/sdl/runs/${encodeURIComponent(runName)}/inputs/upload`
    const headers: Record<string, string> = {}
    if (this.token) headers.Authorization = `Bearer ${this.token}`

    const response = await fetch(url, { method: 'POST', headers, body: formData })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Upload failed: HTTP ${response.status}`)
    }
    return response.json()
  }

  deleteSdlInput = async (runName: string, inputPath: string): Promise<{ status: string; path: string }> => {
    return this.request<{ status: string; path: string }>(
      `/api/v1/admin/sdl/runs/${encodeURIComponent(runName)}/inputs/${inputPath}`,
      { method: 'DELETE' },
    )
  }

  syncSotToSdlRun = async (runName: string, req: SdlSotSyncRequest): Promise<SdlSotSyncResponse> => {
    return this.request<SdlSotSyncResponse>(`/api/v1/admin/sdl/runs/${encodeURIComponent(runName)}/sot-sync`, {
      method: 'POST',
      body: JSON.stringify(req),
    })
  }

  listSdlOutputs = async (runName: string): Promise<SdlOutputsResponse> => {
    return this.request<SdlOutputsResponse>(`/api/v1/admin/sdl/runs/${encodeURIComponent(runName)}/outputs`)
  }

  downloadSdlOutput = async (runName: string, artifactPath: string): Promise<void> => {
    const url = `${this.baseURL}/api/v1/admin/sdl/runs/${encodeURIComponent(runName)}/outputs/${artifactPath}`
    const headers: Record<string, string> = {}
    if (this.token) headers.Authorization = `Bearer ${this.token}`
    const response = await fetch(url, { headers })
    if (!response.ok) throw new Error(`Failed to download artifact: HTTP ${response.status}`)
    const blob = await response.blob()
    const filename = artifactPath.split('/').pop() || 'artifact'
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = filename
    a.click()
    URL.revokeObjectURL(a.href)
  }

  // Fetch an SDL output artifact as parsed JSON (used by Import to tenant).
  getSdlOutputJson = async (runName: string, artifactPath: string): Promise<Record<string, any>> => {
    const url = `${this.baseURL}/api/v1/admin/sdl/runs/${encodeURIComponent(runName)}/outputs/${artifactPath}`
    const headers: Record<string, string> = {}
    if (this.token) headers.Authorization = `Bearer ${this.token}`
    const response = await fetch(url, { headers })
    if (!response.ok) throw new Error(`Failed to fetch artifact: HTTP ${response.status}`)
    return response.json()
  }

  // ─── SOT File Management endpoints ──────────────────────────────────────────

  listSOTFiles = async (subdomain: string, path: string = ''): Promise<SOTListResponse> => {
    const params = path ? `?path=${encodeURIComponent(path)}` : ''
    return this.request<SOTListResponse>(`/api/v1/admin/tenants/${encodeURIComponent(subdomain)}/sot/files${params}`)
  }

  uploadSOTFiles = async (subdomain: string, path: string, files: File[]): Promise<SOTUploadResponse> => {
    const formData = new FormData()
    formData.append('path', path)
    files.forEach((file) => formData.append('files', file))

    const url = `${this.baseURL}/api/v1/admin/tenants/${encodeURIComponent(subdomain)}/sot/files/upload`
    const headers: Record<string, string> = {}
    if (this.token) headers.Authorization = `Bearer ${this.token}`

    const response = await fetch(url, { method: 'POST', headers, body: formData })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Upload failed: HTTP ${response.status}`)
    }
    return response.json()
  }

  downloadSOTFile = async (subdomain: string, path: string): Promise<void> => {
    const url = `${this.baseURL}/api/v1/admin/tenants/${encodeURIComponent(subdomain)}/sot/files/download?path=${encodeURIComponent(path)}`
    const headers: Record<string, string> = {}
    if (this.token) headers.Authorization = `Bearer ${this.token}`

    const response = await fetch(url, { headers })
    if (!response.ok) throw new Error(`Download failed: HTTP ${response.status}`)

    const blob = await response.blob()
    const filename = path.split('/').pop() || 'download'
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = filename
    a.click()
    URL.revokeObjectURL(a.href)
  }

  deleteSOTFiles = async (subdomain: string, paths: string[]): Promise<SOTDeleteResponse> => {
    return this.request<SOTDeleteResponse>(`/api/v1/admin/tenants/${encodeURIComponent(subdomain)}/sot/files`, {
      method: 'DELETE',
      body: JSON.stringify({ paths }),
    })
  }

  createSOTFolder = async (subdomain: string, path: string, name: string): Promise<{ path: string; name: string }> => {
    return this.request<{ path: string; name: string }>(`/api/v1/admin/tenants/${encodeURIComponent(subdomain)}/sot/folders`, {
      method: 'POST',
      body: JSON.stringify({ path, name }),
    })
  }

  // ── Entity Model (AI API, webhook secret) ───────────────────────────────────

  getEntityModel = async (tenantId: string): Promise<EntityModelVersionsResponse> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/admin/tenants/${encodeURIComponent(tenantId)}/entity-model`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }

    const response = await fetch(url, { headers })
    if (!response.ok) throw new Error(`Failed to fetch entity model: HTTP ${response.status}`)
    return response.json()
  }

  uploadEntityModel = async (tenantId: string, modelData: Record<string, any>): Promise<EntityModelVersion> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/admin/tenants/${encodeURIComponent(tenantId)}/entity-model`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ model_data: modelData }),
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Upload failed: HTTP ${response.status}`)
    }
    return response.json()
  }


  // AI Assistant (AI API direct). The tenant must be sent explicitly since the
  // admin UI is not tenant-scoped by domain. Auth is the super-admin JWT +
  // X-Tenant-ID header (zero middleware changes on the AI API); the webhook secret
  // and body tenant_id are kept as belt-and-suspenders.
  queryAIAssistant = async (params: AIAssistantQueryParams): Promise<AIAssistantQueryResponse> => {
    const AI_API_URL = (import.meta as any).env?.VITE_TENANT_API_URL || 'http://localhost:8000'
    const url = `${AI_API_URL}/ai-assistant/query`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Tenant-ID': params.tenant_id,
      'X-Webhook-Secret': (import.meta as any).env?.VITE_WEBHOOK_SECRET || 'your-webhook-secret-key',
    }
    if (this.token) headers.Authorization = `Bearer ${this.token}`

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        tenant_id: params.tenant_id,
        message: params.message,
        context_key: params.context_key,
        context_params: params.context_params,
        conversation_history: params.conversation_history,
      }),
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `AI Assistant request failed: HTTP ${response.status}`)
    }
    return response.json()
  }

}

export const apiClient = new ApiClient()

// ── Entity Model types ──────────────────────────────────────────────────────

// Flat shape consumed by the EntityModelColumns visualization. Produced by
// toFlatEntityModel() from a raw model_data blob (which may be branch-based).
export interface EntityModelJson {
  company: string
  total_entities: number
  entities: Record<string, any>[]
}

// A single saved version, as returned by the backend. model_data is the raw
// entity-model blob (branch-based or already flat) — left loosely typed.
export interface EntityModelVersion {
  id: string
  tenant_id: string
  model_data: Record<string, any>
  created_at: string
}

// GET response: backend keeps at most 2 versions (newest = current).
export interface EntityModelVersionsResponse {
  tenant_id: string
  versions: EntityModelVersion[]
  count: number
}

// AI Assistant
export interface AIAssistantQueryParams {
  tenant_id: string;
  message: string;
  context_key: string;
  context_params?: { date_from?: string; date_to?: string };
  conversation_history?: Array<{ role: 'user' | 'assistant'; content: string }>;
}

export interface AIAssistantQueryResponse {
  response: string;
  context_key: string;
  metadata: Record<string, any>;
  session_contact?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
}
